import { shortenAddress } from './chunk-DRKG2ORU.js';
import { NATIVE_TOKEN, toCanonicalChainId, resolveChain } from './chunk-3K2HDD4E.js';
import { z } from 'zod';

var FLOW_SHAPES = ["sweep", "consolidation", "peel"];
var TERMINUS_CLASSES = ["exchange", "cefi", "bridge", "mixer", "sanctioned", "contract"];
var TERMINUS_LABELS = {
  exchange: "Exchange",
  cefi: "Custodial service",
  bridge: "Bridge",
  mixer: "Mixer",
  sanctioned: "Sanctioned address",
  // `CustodyClass.CONTRACT`: the class this list did not have, so 111 sandbox
  // paths into a Coinbase paymaster rendered as "coinbase · contract" beside
  // the real Coinbase deposits — read as a cash-out, which it is not.
  contract: "Venue contract"
};
var TERMINUS_NOTES = {
  exchange: "An exchange-labelled wallet received funds. This does not establish a sale, fiat withdrawal or the identity of the account holder.",
  cefi: "A custodial service that is not an exchange (a broker, payment processor or OTC desk). Same consequence: custody.",
  bridge: "A bridge endpoint. Continuity requires a verified counterpart transaction; receipt here alone does not establish completion on another chain.",
  mixer: "A privacy boundary. The observed transfer does not link a deposit to a withdrawal; amount or timing similarity alone is insufficient.",
  sanctioned: "An address on a sanctions list. Not custody, but the destination a compliance reader needs first.",
  contract: "A venue's own contract (a router, paymaster or token), not its custody. Funds passed through it; this is not a cash-out."
};
var TERMINUS_PHRASES = {
  exchange: "an exchange",
  cefi: "a custodial service",
  bridge: "a bridge",
  mixer: "a mixer",
  sanctioned: "a sanctioned address",
  contract: "a venue contract"
};
function terminusPhrase(terminusClass) {
  const key = (terminusClass ?? "").trim().toLowerCase();
  return TERMINUS_PHRASES[key] ?? "";
}
var CUSTODY_TERMINUS_CLASSES = /* @__PURE__ */ new Set(["exchange", "cefi"]);
var wireString = z.string().nullish().transform((value) => value ?? "");
var hopSchema = z.object({
  decimals: z.number().int().min(0).max(255).nullable().optional(),
  index: z.number().default(0),
  from_address: wireString,
  to_address: wireString,
  amount: z.string().nullish().transform((value) => value ?? "0"),
  token: wireString,
  tx_hash: wireString,
  tx_id: wireString,
  block_number: z.number().nullable().default(null),
  shape: wireString,
  exchange_id: wireString,
  terminus_class: wireString,
  terminus_entity_id: wireString
});
var attrsSchema = z.object({
  decimals: z.number().int().min(0).max(255).nullable().optional(),
  terminus_class: wireString,
  terminus_entity_id: wireString,
  // Exchange-only and older than the generalised walk. Read as a fallback so a
  // row written before `terminus_entity_id` existed still names its venue.
  exchange_id: wireString,
  exchange_address: wireString,
  deposit_wallet: wireString,
  origin_wallet: wireString,
  hop_count: z.number().default(0),
  token: wireString,
  amount: z.string().nullish().transform((value) => value ?? "0"),
  forward_ratio: z.number().default(0),
  shapes: z.array(z.string()).default([]),
  block_number: z.number().nullable().default(null),
  chain_confidence: z.number().default(0),
  hops: z.array(hopSchema).default([]),
  chain_id: wireString,
  // The venue's display name, added at READ time by the groups API from the
  // exchange registry (`commons.grouping.exchange_flow.peel_chain_display_attrs`).
  terminus_name: wireString
});
var groupSchema = z.object({
  /**
   * The saga's own id. `id` is the field `TransactionGroup` carries; `group_id`
   * is the CATALOG group a row belongs to, and on these rows it is always null
   * — measured across all 259 in sandbox.
   *
   * Reading `group_id` gave every path an empty id, which had three visible
   * consequences: React was handed the same `key` for every row in a terminus
   * (300 duplicate-key warnings; "children may be duplicated and/or omitted"),
   * the hop detail rendered "Group  · complete" with a hole in it, and
   * `foldFlowTermini`'s dedupe — guarded on the id being truthy — never ran.
   */
  id: wireString,
  group_id: wireString,
  saga_kind: wireString,
  state: wireString,
  chain_id: wireString,
  attrs: z.unknown().optional(),
  // Each leg names its chain. Read only for the chain fallback below, and
  // loosely: a malformed member must not cost the path.
  members: z.array(z.unknown()).nullish().transform((value) => value ?? [])
});
function memberChain(members) {
  const chains = /* @__PURE__ */ new Set();
  for (const member of members) {
    const chain = member && typeof member === "object" ? member.chain_id : null;
    if (typeof chain === "string" && chain) chains.add(chain);
  }
  return chains.size === 1 ? [...chains][0] : "";
}
function canonicalOrRaw(chain) {
  return toCanonicalChainId(chain) ?? chain;
}
var PEEL_CHAIN_SAGA_KIND = "flow.peel_chain";
function asShape(value) {
  return FLOW_SHAPES.includes(value) ? value : "";
}
function parseFlowPath(raw) {
  const group = groupSchema.safeParse(raw);
  if (!group.success) return null;
  const attrs = attrsSchema.safeParse(group.data.attrs ?? {});
  if (!attrs.success) return null;
  const a = attrs.data;
  const terminusEntityId = a.terminus_entity_id || a.exchange_id;
  const depositWallet = a.deposit_wallet;
  if (!depositWallet || !a.terminus_class && !terminusEntityId) return null;
  return {
    groupId: group.data.id || group.data.group_id,
    chainId: canonicalOrRaw(group.data.chain_id || a.chain_id || memberChain(group.data.members)),
    decimals: a.decimals,
    // Older rows predate the generalised walk and stamped only `exchange_id`;
    // reading them as `exchange` keeps them on the board instead of silently
    // dropping every path written before the terminus classes existed.
    terminusClass: a.terminus_class || (terminusEntityId ? "exchange" : ""),
    terminusEntityId,
    terminusName: a.terminus_name,
    terminusAddress: a.exchange_address,
    depositWallet,
    originWallet: a.origin_wallet,
    hopCount: a.hop_count || a.hops.length,
    token: a.token || NATIVE_TOKEN,
    amount: a.amount,
    confidence: a.chain_confidence,
    shapes: a.shapes,
    blockNumber: a.block_number,
    state: group.data.state,
    hops: a.hops.map((hop) => ({
      txId: hop.tx_id,
      decimals: hop.decimals,
      index: hop.index,
      from: hop.from_address,
      to: hop.to_address,
      amount: hop.amount,
      token: hop.token,
      txHash: hop.tx_hash,
      blockNumber: hop.block_number,
      shape: asShape(hop.shape)
    }))
  };
}
function parseFlowPathsWithRejects(rows) {
  const paths = [];
  let rejected = 0;
  for (const row of rows) {
    const path = parseFlowPath(row);
    if (path) paths.push(path);
    else rejected += 1;
  }
  return { paths, rejected };
}
function parseFlowPaths(rows) {
  return parseFlowPathsWithRejects(rows).paths;
}
function terminusLabel(path) {
  if (path.terminusName) return path.terminusName;
  if (path.terminusEntityId) return path.terminusEntityId;
  return TERMINUS_LABELS[path.terminusClass] ?? path.terminusClass ?? "Unknown";
}
function terminusKey(path) {
  return `${toCanonicalChainId(path.chainId) ?? path.chainId}:${path.terminusClass}:${path.terminusEntityId || path.terminusAddress}`;
}
function flowConfidenceLabel(confidence) {
  if (confidence >= 0.8) return "Strong";
  if (confidence >= 0.6) return "Moderate";
  return "Weak";
}
function formatFlowAmountShort(raw, chainId, token = NATIVE_TOKEN, info = {}) {
  const chain = resolveChain(chainId);
  const native = token === NATIVE_TOKEN;
  const precision = info.decimals ?? (native ? chain?.decimals : void 0);
  const unit = native ? chain?.nativeToken ?? NATIVE_TOKEN : info.symbol || shortToken(token);
  let amount;
  try {
    amount = BigInt(raw);
  } catch {
    return `Unknown amount (${unit})`;
  }
  const negative = amount < 0n;
  const absolute = negative ? -amount : amount;
  const sign = negative ? "\u2212" : "";
  if (precision == null || !Number.isInteger(precision) || precision < 0 || precision > 255) {
    return `${sign}${groupDigits(absolute)} raw units (${unit})`;
  }
  const scale = 10n ** BigInt(precision);
  const whole = absolute / scale;
  const places = whole >= 1000n ? 2 : whole >= 1n ? 4 : 6;
  const fractionDigits = precision ? (absolute % scale).toString().padStart(precision, "0").slice(0, Math.min(places, precision)) : "";
  const fraction = fractionDigits.replace(/0+$/, "");
  if (whole === 0n && !fraction && absolute > 0n) {
    return `${sign}<0.${"0".repeat(Math.max(0, places - 1))}1 ${unit}`;
  }
  return `${sign}${groupDigits(whole)}${fraction ? `.${fraction}` : ""} ${unit}`;
}
function groupDigits(value) {
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}
function shortToken(token) {
  return /^0x[0-9a-fA-F]{40}$/.test(token) ? shortenAddress(token) : token;
}
function formatFlowAmount(raw, chainId, token = NATIVE_TOKEN, decimals) {
  const chain = resolveChain(chainId);
  const precision = decimals ?? (token === NATIVE_TOKEN ? chain?.decimals : void 0);
  const unit = token === NATIVE_TOKEN ? chain?.nativeToken ?? NATIVE_TOKEN : token;
  try {
    const amount = BigInt(raw);
    if (precision == null || !Number.isInteger(precision) || precision < 0 || precision > 255) return `${amount} base units (${unit})`;
    const negative = amount < 0n;
    const absolute = negative ? -amount : amount;
    const scale = 10n ** BigInt(precision);
    const fraction = precision ? (absolute % scale).toString().padStart(precision, "0").replace(/0+$/, "") : "";
    return `${negative ? "\u2212" : ""}${absolute / scale}${fraction ? `.${fraction}` : ""} ${unit}`;
  } catch {
    return `Unknown amount (${unit})`;
  }
}

export { CUSTODY_TERMINUS_CLASSES, FLOW_SHAPES, PEEL_CHAIN_SAGA_KIND, TERMINUS_CLASSES, TERMINUS_LABELS, TERMINUS_NOTES, TERMINUS_PHRASES, flowConfidenceLabel, formatFlowAmount, formatFlowAmountShort, parseFlowPath, parseFlowPaths, parseFlowPathsWithRejects, terminusKey, terminusLabel, terminusPhrase };
//# sourceMappingURL=chunk-Z4OLEMZ6.js.map
//# sourceMappingURL=chunk-Z4OLEMZ6.js.map